import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  FileCheck2,
  ClipboardCheck,
  Tag,
  Check,
  Zap,
  MessagesSquare,
  CalendarClock,
  UserRound,
  MapPin,
  Briefcase,
  ArrowRight,
  Phone,
} from "lucide-react";
import SEO from "@/components/SEO";
import TrustMarquee from "@/components/TrustMarquee";
import FAQ from "@/components/FAQ";
import { FadeUp, Chapter, CTAButton, CTABand, EASE } from "@/components/Reveal";
import { Pricing, Coverage, Testimonials, ZakelijkTeaser } from "@/components/Sections";
import { FAQ_ITEMS, IMAGES } from "@/data/content";

const HERO_LINES = ["Snel en zorgvuldig", "uw energielabel", "geregeld."];

const HERO_TRUST = [
  { icon: ClipboardCheck, label: "EP-W woningopname" },
  { icon: FileCheck2, label: "Registratie in EP-online" },
  { icon: Tag, label: "Tarieven vanaf €139 incl. btw" },
];

const WHY = [
  { icon: Zap, title: "Snelle beschikbaarheid", text: "Op korte termijn een opname nodig? Wij denken direct met u mee." },
  { icon: MessagesSquare, title: "Duidelijke communicatie", text: "U weet altijd waar u aan toe bent, van aanvraag tot registratie." },
  { icon: ClipboardCheck, title: "Zorgvuldige woningopname", text: "Elke woning wordt opgenomen volgens de geldende EP-methodiek." },
  { icon: Tag, title: "Transparante prijzen", text: "Vaste introductietarieven, inclusief btw en registratie in EP-online." },
  { icon: CalendarClock, title: "Flexibele planning", text: "De woningopname vindt plaats op een moment dat u uitkomt." },
  { icon: UserRound, title: "Persoonlijk contact", text: "Eén vast aanspreekpunt tijdens het hele traject." },
  { icon: MapPin, title: "Actief in Rotterdam en omgeving", text: "Lokaal bereikbaar in heel de regio Rotterdam en Zuid-Holland." },
  { icon: Briefcase, title: "Particulier en zakelijk", text: "Voor woningeigenaren, makelaars, verhuurders en vastgoedpartijen." },
];

const STEPS = [
  {
    n: "01",
    title: "U vraagt een energielabel aan",
    text: "Vul het online formulier in met uw woninggegevens en gewenste datum. Dat kost u slechts enkele minuten.",
  },
  {
    n: "02",
    title: "Wij plannen de woningopname",
    text: "Wij nemen contact met u op en plannen samen een moment dat u het beste uitkomt.",
  },
  {
    n: "03",
    title: "Zorgvuldige opname",
    text: "De woning wordt zorgvuldig opgenomen volgens de geldende EP-methodiek.",
  },
  {
    n: "04",
    title: "Verwerking en registratie",
    text: "Het energielabel wordt verwerkt en geregistreerd in EP-online. U ontvangt bericht zodra het beschikbaar is.",
  },
];

const CHECKLIST = [
  "Heldere introductietarieven, zonder verrassingen achteraf",
  "Woningopname volgens de geldende EP-methodiek",
  "Registratie in de landelijke database EP-online",
  "Persoonlijk aanspreekpunt tijdens het hele traject",
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  return (
    <>
      <SEO
        title="Energielabel aanvragen in Rotterdam | Directelabels — EP-W woningopnames"
        description="Directelabels voert professionele EP-W woningopnames uit in Rotterdam en Zuid-Holland. Vraag eenvoudig een energielabel aan voor uw woning. Introductietarieven vanaf €139 incl. btw."
        path="/"
      />

      <section ref={heroRef} data-testid="hero-section" className="relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-5 pb-20 pt-14 md:px-8 md:pb-28 md:pt-24">
          <div className="grid items-center gap-14 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: EASE }}
                className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-energy-pale px-4 py-2"
              >
                <span className="h-2 w-2 rounded-full bg-primary" aria-hidden="true" />
                <span className="text-xs font-bold uppercase tracking-[0.18em] text-energy-deep">
                  EP-W woningopnames · Rotterdam en omgeving
                </span>
              </motion.div>

              <h1 data-testid="hero-title" className="mt-8 font-display text-5xl font-semibold leading-[1.02] tracking-tighter text-ink md:text-6xl lg:text-7xl">
                {HERO_LINES.map((line, i) => (
                  <span key={line} className="block overflow-hidden pb-1">
                    <motion.span
                      className="block"
                      initial={{ y: "110%" }}
                      animate={{ y: 0 }}
                      transition={{ duration: 0.9, delay: 0.15 + i * 0.13, ease: EASE }}
                    >
                      {i === 1 ? <span className="text-primary">{line}</span> : line}
                    </motion.span>
                  </span>
                ))}
              </h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6, ease: EASE }}
                className="mt-7 max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg"
              >
                Directelabels voert professionele EP-W woningopnames uit en helpt u aan een energielabel voor uw woning:
                bij verkoop of verhuur, of gewoon om inzicht te krijgen in de energieprestatie. Duidelijke tarieven,
                zorgvuldige opname en registratie in EP-online.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.72, ease: EASE }}
                className="mt-9 flex flex-wrap items-center gap-4"
              >
                <CTAButton to="/aanvragen" testid="hero-cta-aanvragen">
                  Energielabel aanvragen
                </CTAButton>
                <CTAButton to="/contact" testid="hero-cta-offerte" variant="outline">
                  Offerte aanvragen
                </CTAButton>
                <Link
                  to="/contact"
                  data-testid="hero-cta-contact"
                  className="inline-flex items-center gap-2 text-sm font-bold text-primary transition-colors duration-200 hover:text-energy-light md:text-base"
                >
                  <Phone size={16} />
                  Direct contact
                </Link>
              </motion.div>

              <motion.ul
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.9 }}
                className="mt-10 flex flex-wrap gap-x-8 gap-y-3"
              >
                {HERO_TRUST.map((t) => (
                  <li key={t.label} className="flex items-center gap-2 text-sm font-semibold text-ink/70">
                    <t.icon size={17} className="text-primary" aria-hidden="true" />
                    {t.label}
                  </li>
                ))}
              </motion.ul>
            </div>

            <div className="relative lg:col-span-5">
              <motion.div
                initial={{ opacity: 0, scale: 0.96, y: 30 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 1.1, delay: 0.35, ease: EASE }}
                className="relative"
              >
                <motion.div style={{ y: imgY }} className="relative">
                  <div className="overflow-hidden rounded-t-[9rem] rounded-b-[2rem] border border-black/5 shadow-2xl shadow-energy-deep/10">
                    <img
                      src={IMAGES.heroHouse}
                      alt="Moderne Nederlandse woning waarvoor Directelabels een energielabel opneemt"
                      className="aspect-[4/5] w-full object-cover"
                      data-testid="hero-image"
                    />
                  </div>
                  <div
                    data-testid="hero-badge"
                    className="absolute -left-4 bottom-10 rounded-2xl border border-black/5 bg-white p-5 shadow-xl md:-left-10"
                  >
                    <div className="flex items-center gap-3">
                      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-energy-pale text-primary">
                        <FileCheck2 size={22} />
                      </span>
                      <div>
                        <p className="font-display text-sm font-semibold text-ink">Geregistreerd</p>
                        <p className="text-xs font-medium text-muted-foreground">in EP-online (RVO)</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <TrustMarquee />

      <section data-testid="why-section" className="mx-auto max-w-7xl px-5 py-24 md:px-8 md:py-32">
        <Chapter
          number="01"
          label="Waarom Directelabels"
          title="Zorgvuldig geregeld, van aanvraag tot registratie"
          description="Wij ontzorgen woningeigenaren, makelaars en verhuurders met een duidelijk proces, vaste tarieven en persoonlijke begeleiding."
        />
        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {WHY.map((w, i) => (
            <FadeUp key={w.title} delay={(i % 4) * 0.08}>
              <div
                data-testid={`why-card-${i}`}
                className="group h-full rounded-3xl border border-black/5 bg-white p-7 shadow-sm transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-lg"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-energy-pale text-primary transition-colors duration-300 group-hover:bg-primary group-hover:text-white">
                  <w.icon size={20} />
                </span>
                <h3 className="mt-5 font-display text-lg font-medium tracking-tight text-ink">{w.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{w.text}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      <section id="hoe-werkt-het" data-testid="process-section" className="border-y border-black/5 bg-white">
        <div className="mx-auto max-w-7xl px-5 py-24 md:px-8 md:py-32">
          <Chapter
            number="02"
            label="Hoe werkt het?"
            title="In vier stappen naar uw energielabel"
            description="Van online aanvraag tot geregistreerd label: zo eenvoudig kan het zijn."
          />
          <div className="mt-16 grid gap-10 md:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <FadeUp key={s.n} delay={i * 0.1}>
                <div data-testid={`process-step-${i}`} className="relative">
                  <span className="font-display text-6xl font-light text-primary/20">{s.n}</span>
                  <div className="mt-4 h-px w-full bg-gradient-to-r from-primary/30 to-transparent" aria-hidden="true" />
                  <h3 className="mt-5 font-display text-lg font-medium tracking-tight text-ink md:text-xl">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.text}</p>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.3} className="mt-14">
            <CTAButton to="/aanvragen" testid="process-cta-aanvragen">
              Start uw aanvraag
            </CTAButton>
          </FadeUp>
        </div>
      </section>

      <Pricing number="03" />

      <Coverage number="04" />

      <section data-testid="trust-section" className="border-y border-black/5 bg-energy-pale/60">
        <div className="mx-auto max-w-7xl px-5 py-24 md:px-8 md:py-32">
          <div className="grid items-center gap-14 lg:grid-cols-12">
            <div className="order-2 lg:order-1 lg:col-span-5">
              <FadeUp>
                <div className="overflow-hidden rounded-b-[9rem] rounded-t-[2rem] border border-black/5 shadow-xl shadow-energy-deep/10">
                  <img
                    src={IMAGES.inspector}
                    alt="Adviseur van Directelabels maakt aantekeningen tijdens een woningopname"
                    className="aspect-[4/5] w-full object-cover"
                    data-testid="trust-image"
                    loading="lazy"
                  />
                </div>
              </FadeUp>
            </div>
            <div className="order-1 lg:order-2 lg:col-span-7">
              <Chapter
                number="05"
                label="Betrouwbaar en zorgvuldig"
                title="Uw woning in vertrouwde handen"
                description="Een energielabel is een officieel document dat bij uw woning hoort. Daarom werkt Directelabels zorgvuldig, met oog voor detail en duidelijke communicatie."
              />
              <ul className="mt-10 space-y-4">
                {CHECKLIST.map((item, i) => (
                  <FadeUp key={item} delay={0.1 + i * 0.08}>
                    <li data-testid={`trust-check-${i}`} className="flex items-start gap-4 rounded-2xl border border-black/5 bg-white p-5">
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-white">
                        <Check size={14} strokeWidth={3} />
                      </span>
                      <span className="text-sm font-semibold leading-relaxed text-ink md:text-base">{item}</span>
                    </li>
                  </FadeUp>
                ))}
              </ul>
              <FadeUp delay={0.4} className="mt-10">
                <CTAButton to="/contact" testid="trust-cta-offerte">
                  Offerte aanvragen
                </CTAButton>
              </FadeUp>
            </div>
          </div>
        </div>
      </section>

      <ZakelijkTeaser />

      <Testimonials number="06" />

      <section data-testid="faq-section" className="mx-auto max-w-7xl px-5 py-24 md:px-8 md:py-32">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Chapter
              number="07"
              label="Veelgestelde vragen"
              title="Alles over het energielabel"
              description="Antwoorden op de vragen die ons het vaakst worden gesteld. Staat uw vraag er niet bij? Neem gerust contact op."
            />
            <FadeUp delay={0.2} className="mt-8 flex flex-wrap items-center gap-6">
              <CTAButton to="/contact" testid="faq-cta-contact" variant="outline">
                Stel uw vraag
              </CTAButton>
              <Link
                to="/faq"
                data-testid="faq-link-all"
                className="group inline-flex items-center gap-2 text-sm font-bold text-primary transition-colors duration-200 hover:text-energy-light"
              >
                Alle veelgestelde vragen
                <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
            </FadeUp>
          </div>
          <div className="lg:col-span-7">
            <FadeUp delay={0.1}>
              <FAQ items={FAQ_ITEMS.slice(0, 5)} />
            </FadeUp>
          </div>
        </div>
      </section>

      <CTABand />
    </>
  );
}
